import { useEffect, useRef, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import * as Dialog from '@radix-ui/react-dialog'
import { Sidebar } from './sidebar'
import { MobileHeader } from './mobile-header'
import { CommandPalette } from '@/components/command-palette/command-palette'
import { Toaster } from '@/components/ui/toaster'
import { usePaletteStore } from '@/stores/palette-store'
import { useAuthStore } from '@/stores/auth-store'
import { authApi } from '@/lib/api'

export function Shell() {
  const toggle = usePaletteStore((s) => s.toggle)
  const clear = useAuthStore((s) => s.clear)
  const navigate = useNavigate()
  const [mobileOpen, setMobileOpen] = useState(false)
  const location = useLocation()

  // Keep latest pathname in a ref so keyboard handlers always see the current value
  const pathnameRef = useRef(location.pathname)
  useEffect(() => {
    pathnameRef.current = location.pathname
  }, [location.pathname])

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  // Global keyboard shortcuts
  useEffect(() => {
    // Chord state — stored in refs to avoid stale closures
    let pendingKey: string | null = null
    let pendingTimer: ReturnType<typeof setTimeout> | null = null

    function resetChord() {
      pendingKey = null
      if (pendingTimer) {
        clearTimeout(pendingTimer)
        pendingTimer = null
      }
    }

    function startChord(key: string) {
      pendingKey = key
      if (pendingTimer) clearTimeout(pendingTimer)
      pendingTimer = setTimeout(resetChord, 1500)
    }

    function currentSlug(): string | null {
      const m = pathnameRef.current.match(/^\/workspaces\/([^/]+)/)
      return m?.[1] ?? null
    }

    const handler = (e: KeyboardEvent) => {
      // Skip when typing in input fields
      const tag = (e.target as HTMLElement).tagName
      if (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        (e.target as HTMLElement).isContentEditable
      ) {
        resetChord()
        return
      }

      // ── ⌘K / Ctrl+K → command palette ──
      if ((e.metaKey || e.ctrlKey) && e.code === 'KeyK') {
        e.preventDefault()
        resetChord()
        toggle()
        return
      }

      // ── Alt+Shift+Q → log out ──
      if (e.altKey && e.shiftKey && e.code === 'KeyQ') {
        e.preventDefault()
        resetChord()
        authApi.logout().then(() => clear())
        return
      }

      // Ignore other modified key combos for chord detection
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return

      // ── Chord: G → S → Settings ──
      if (pendingKey === 'g' && e.code === 'KeyS') {
        e.preventDefault()
        resetChord()
        const slug = currentSlug()
        navigate(slug ? `/workspaces/${slug}/settings` : '/workspaces')
        return
      }

      // ── Chord: O → W → Switch workspace ──
      if (pendingKey === 'o' && e.code === 'KeyW') {
        e.preventDefault()
        resetChord()
        navigate('/workspaces')
        return
      }

      // Start chord on G or O
      if (e.code === 'KeyG') {
        startChord('g')
        return
      }
      if (e.code === 'KeyO') {
        startChord('o')
        return
      }

      // Any other key cancels the pending chord
      resetChord()
    }

    window.addEventListener('keydown', handler)
    return () => {
      window.removeEventListener('keydown', handler)
      if (pendingTimer) clearTimeout(pendingTimer)
    }
  }, [toggle, clear, navigate])

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background md:flex-row">
      {/* Desktop sidebar — hidden on mobile */}
      <div className="hidden md:flex">
        <Sidebar />
      </div>

      {/* Mobile sidebar drawer (Radix Dialog) */}
      <Dialog.Root open={mobileOpen} onOpenChange={setMobileOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-black/50 md:hidden" />
          <Dialog.Content
            aria-describedby={undefined}
            className="fixed left-0 top-0 z-40 h-full md:hidden"
          >
            <Dialog.Title className="sr-only">Menu</Dialog.Title>
            <Sidebar onClose={() => setMobileOpen(false)} />
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Content column */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Mobile header — visible only below md */}
        <MobileHeader onHamburger={() => setMobileOpen(true)} />

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-7xl p-6">
            <Outlet />
          </div>
        </main>
      </div>

      <CommandPalette />
      <Toaster />
    </div>
  )
}
